import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, RefreshCw } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useApp } from '../../context/AppContext';
import { useAnthropicChat } from '../../hooks/useAnthropicChat';
import './Admin.css';

const STATUS_MAP = {
  completed: { label: 'مكتمل', cls: 'status-done' },
  'in-progress': { label: 'جارٍ', cls: 'status-progress' },
  waiting: { label: 'انتظار', cls: 'status-waiting' },
};

// مكون صغير يحلل جلسة واحدة
function SessionAnalyzer({ session, agent, onDone }) {
  const { analyzeInterview } = useAnthropicChat(
    agent || { name: '', objective: '', prompt: '', questionCount: 7, outputFields: [] }
  );
  const { updateSession } = useApp();
  const [analyzing, setAnalyzing] = useState(false);

  const runAnalysis = useCallback(async () => {
    if (analyzing || !session.messages?.length || !agent) return;
    setAnalyzing(true);
    try {
      const analysis = await analyzeInterview(session.messages);
      if (analysis) {
        updateSession(session.id, { analysis });
        onDone(session.id);
      }
    } catch (e) {
      console.error('Analysis error:', e);
    } finally {
      setAnalyzing(false);
    }
  }, [session.id]);

  useEffect(() => { runAnalysis(); }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div className="spinner" style={{ width: '13px', height: '13px', borderTopColor: 'var(--brown)' }} />
      <span style={{ fontSize: '11px', color: 'var(--brown-muted)' }}>
        {analyzing ? 'جاري التحليل...' : 'في الانتظار'}
      </span>
    </div>
  );
}

export default function ResultsPage() {
  const { sessions, agents, updateSession, loadData } = useApp();
  const [filter, setFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [search, setSearch] = useState('');

  // الجلسات المكتملة بدون تحليل
  const pendingAnalysis = sessions.filter(
    s => s.status === 'completed' && !s.analysis && s.messages?.length > 0
  );

  // إذا في مقابلات تنتظر تحليل — حدّث كل 5 ثوان
  useEffect(() => {
    if (pendingAnalysis.length === 0) return;
    const interval = setInterval(() => { loadData(); }, 5000);
    return () => clearInterval(interval);
  }, [pendingAnalysis.length]);

  const handleAnalyzed = () => { loadData(); };

  const filtered = sessions.filter(s => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (agentFilter !== 'all' && s.agentId !== agentFilter) return false;
    if (search && !s.id.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const exportCSV = () => {
    const rows = [['ID', 'الوكيل', 'الحالة', 'التاريخ', 'المدة (دقيقة)', 'نقاط الألم', 'درجة الخطورة', 'احتياجات المستخدم', 'الفرص', 'اقتباسات', 'الملخص']];
    filtered.forEach(s => {
      const agent = agents.find(a => a.id === s.agentId);
      rows.push([
        s.id,
        agent?.name || '',
        s.status === 'completed' ? 'مكتمل' : s.status === 'in-progress' ? 'جارٍ' : 'انتظار',
        new Date(s.createdAt).toLocaleDateString('ar-SA'),
        s.durationMinutes || '',
        s.analysis?.painPoints?.map(p => `${p.label} (${p.severity})`).join(' | ') || '',
        s.analysis?.severityScore || '',
        s.analysis?.userNeeds?.join(' | ') || '',
        s.analysis?.opportunities?.join(' | ') || '',
        s.analysis?.keyQuotes?.map(q => q.text).join(' | ') || '',
        s.analysis?.summary || '',
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `interviews-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout
      title="نتائج المقابلات"
      subtitle={`${filtered.length} مقابلة`}
      actions={
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {pendingAnalysis.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--amber-bg)', padding: '6px 12px', borderRadius: 'var(--radius-full)', fontSize: '12px', color: 'var(--amber-text)' }}>
              <div className="spinner" style={{ width: '12px', height: '12px', borderTopColor: 'var(--amber-text)', borderColor: 'rgba(133,79,11,0.2)' }} />
              جاري تحليل {pendingAnalysis.length} مقابلة...
            </div>
          )}
          <button className="btn btn-ghost btn-sm" onClick={exportCSV}>
            <Download size={14} />
            تصدير CSV
          </button>
        </div>
      }
    >
      {/* تحليل تلقائي في الخلفية */}
      {pendingAnalysis.map(s => {
        const agent = agents.find(a => a.id === s.agentId);
        return (
          <div key={s.id} style={{ display: 'none' }}>
            <SessionAnalyzer session={s} agent={agent} onDone={handleAnalyzed} />
          </div>
        );
      })}

      {/* Filters */}
      <div className="results-filters">
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--brown-muted)' }} />
          <input
            className="form-input"
            style={{ paddingRight: '30px', width: '180px', padding: '6px 30px 6px 10px' }}
            placeholder="بحث بالرقم..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {['all', 'completed', 'in-progress'].map(f => (
          <button key={f} className={`filter-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'الكل' : f === 'completed' ? 'مكتمل' : 'جارٍ'}
          </button>
        ))}
        <select className="form-select" style={{ width: 'auto', padding: '5px 10px' }} value={agentFilter} onChange={e => setAgentFilter(e.target.value)}>
          <option value="all">كل الوكلاء</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="data-table">
          <thead style={{ background: 'var(--gray-bg)' }}>
            <tr>
              <th>المشارك / الرقم</th>
              <th>الوكيل</th>
              <th>الحالة</th>
              <th>نقاط الألم</th>
              <th>الاقتباس</th>
              <th>الخطورة</th>
              <th>التاريخ</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const agent = agents.find(a => a.id === s.agentId);
              const st = STATUS_MAP[s.status] || STATUS_MAP.waiting;
              const severity = s.analysis?.severityScore;
              const sevCls = severity >= 4 ? 'sev-high' : severity >= 2.5 ? 'sev-mid' : 'sev-low';
              const isPendingAnalysis = s.status === 'completed' && !s.analysis && s.messages?.length > 0;

              return (
                <tr key={s.id}>
                  <td>
                    <Link to={`/admin/results/${s.id}`} style={{ textDecoration: 'none' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--brown)', fontFamily: 'var(--font-mono)' }}>#{s.id}</div>
                      <div style={{ fontSize: '11px', color: 'var(--brown-muted)' }}>{s.durationMinutes ? `${s.durationMinutes} دقيقة` : '—'}</div>
                    </Link>
                  </td>
                  <td style={{ fontSize: '12px', color: 'var(--brown-mid)', maxWidth: '140px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent?.name || '—'}</div>
                  </td>
                  <td><span className={`status ${st.cls}`}>{st.label}</span></td>
                  <td>
                    {isPendingAnalysis ? (
                      <SessionAnalyzer session={s} agent={agent} onDone={handleAnalyzed} />
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                        {(s.analysis?.painPoints || []).slice(0, 2).map((p, i) => (
                          <span key={i} className="tag tag-pain" style={{ fontSize: '11px' }}>{p.label}</span>
                        ))}
                        {!s.analysis && s.status === 'completed' && <span style={{ fontSize: '11px', color: 'var(--brown-muted)' }}>—</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '12px', color: 'var(--brown-muted)', maxWidth: '160px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.analysis?.keyQuotes?.[0]?.text ? `"${s.analysis.keyQuotes[0].text}"` : '—'}
                    </div>
                  </td>
                  <td>
                    {severity ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span className={`severity-dot ${sevCls}`} />
                        <span style={{ fontSize: '12px', fontWeight: 600 }}>{severity.toFixed(1)}</span>
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--brown-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(s.createdAt).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })}
                    <br />
                    {new Date(s.createdAt).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="empty-state"><p>لا توجد نتائج تطابق معايير البحث</p></div>
        )}
      </div>
    </AdminLayout>
  );
}
